import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import type {
  PlatformCategoryDefinition,
  PlatformCategoryId,
  PlatformId,
  PlatformOverviewViewModel,
} from '../../models/fundraising-comparison.models';

@Component({
  standalone: true,
  selector: 'app-organization-receives-chart',
  imports: [CommonModule],
  templateUrl: './organization-receives-chart.component.html',
  host: { class: 'd-block' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrganizationReceivesChartComponent {
  @Input({ required: true }) rows: PlatformOverviewViewModel[] = [];
  @Input({ required: true }) categories: PlatformCategoryDefinition[] = [];
  @Output() quickViewRequested = new EventEmitter<PlatformId>();

  formatMoney(amountCents: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: amountCents % 100 === 0 ? 0 : 2,
    }).format(amountCents / 100);
  }

  formatPercentage(percentage: number): string {
    return `${Number(percentage.toFixed(1))}%`;
  }

  retentionSummary(row: PlatformOverviewViewModel): string {
    const retainedPercentage = this.formatPercentage(row.retentionPercentage);
    return row.organizationReceivesOverageCents > 0
      ? `${retainedPercentage} + ${this.formatMoney(
          row.organizationReceivesOverageCents,
        )}`
      : retainedPercentage;
  }

  rowAriaLabel(row: PlatformOverviewViewModel): string {
    const category = this.categoryFor(row.categoryId);
    const categoryDescription = category ? ` ${category.label}.` : '';
    const configurationDescription = row.configurationSummary
      ? ` Current configuration: ${row.configurationSummary}.`
      : '';
    const supporterPaidDescription = row.supporterPaidIndicator
      ? ` ${this.supporterPaidText(row)}.`
      : '';
    const overageDescription =
      row.organizationReceivesOverageCents > 0
        ? ` This is ${this.formatMoney(
            row.organizationReceivesOverageCents,
          )} above intended donations.`
        : '';

    return `${row.result.platformName} retains ${this.formatPercentage(
      row.retentionPercentage,
    )} of the original online fundraiser, or ${this.formatMoney(
      row.retainedOriginalDonationCents,
    )} of ${this.formatMoney(
      row.result.intendedGiftTotalCents,
    )}. Organization receives ${this.formatMoney(
      row.organizationReceivesCents,
    )} total.${overageDescription}${supporterPaidDescription}${categoryDescription}${configurationDescription} Activate to configure this platform.`;
  }

  supporterPaidText(row: PlatformOverviewViewModel): string {
    const indicator = row.supporterPaidIndicator;
    if (!indicator) return '';
    const amount =
      indicator.amountCents === undefined
        ? ''
        : ` +${this.formatMoney(indicator.amountCents)}`;
    const amountContext = indicator.amountContext
      ? ` ${indicator.amountContext}`
      : '';
    return `${indicator.label}${amount}${amountContext} · ${indicator.detail}`;
  }

  categoryFor(
    categoryId: PlatformCategoryId,
  ): PlatformCategoryDefinition | undefined {
    return this.categories.find((category) => category.id === categoryId);
  }

  trackRow(_: number, row: PlatformOverviewViewModel): string {
    return row.result.platformId;
  }

  trackCategory(_: number, category: PlatformCategoryDefinition): string {
    return category.id;
  }
}
